import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import TableUi from "../../../atoms/Table";
import { useEffect, useRef, useState } from "react";
import { ClientsType } from "../ducks/types";
import { PopUp } from "../../../atoms/PopUp";
import EmptyCard from "../../../atoms/EmptyCard";
import {
  CrossIcon,
  DeleteIcon,
  NeedHelp,
  TickIcon,
} from "../../../atoms/CustomIcons";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getSubcontractorListing,
  removeSubcontractListData,
} from "../ducks/actions";
import {
  changeSubcontractorStatus,
  deleteSubcontractor,
} from "../ducks/services";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { getCompanyFieldsById } from "../../CompanySettings/TagsCategories/ducks/actions";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import Filters from "./Components/Filters";
import DashboardTemplate from "../../../templates/Dashboard";
import Header from "../../../atoms/Header";

export default function Subs() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const navigate = useNavigate();
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord, setTableRecord] = useState<ClientsType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const dispatch = useAppDispatch();
  const subListData = useAppSelector((state) => state?.crewsSubs?.subListData);

  const tagItems = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "SUBCONTRACTORS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string;
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-subcontractor/${tableRecord?.id}/${companyId}`);
          }}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            tableRecord?.status === "active"
              ? setArchivePopUp(true)
              : archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
          icon={
            <Image
              src={`/images/table/${
                tableRecord?.status === "active" ? "archive" : "activate"
              }-icon.svg`}
              preview={false}
            />
          }
        >
          {tableRecord?.status === "active" ? "Archive" : "Activate"}
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />
          Activate
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "archived",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            tableRecord?.status === "active"
              ? setArchivePopUp(true)
              : archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />
          Archived
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<ClientsType> = [
    {
      title: "Subcontractor name",
      dataIndex: "name",
      width: 248,
      render: (text, record) =>
        text ? `${text} ${record?.last_name ?? ""}` : "-",
      fixed: "left",
    },
    {
      title: (
        <Tooltip title="The service areas the subcontractor works in">
          Service lines
        </Tooltip>
      ),
      dataIndex: "service_lines",
      width: 200,
      render: (text) => (text ? text : "-"),
    },
    {
      title: (
        <Tooltip title="Specific services the subcontractor provides">
          Services
        </Tooltip>
      ),
      dataIndex: "service",
      width: 102,
      align: "right",
      render: (text) => (text ? text : "-"),
    },
    {
      title: (
        <Tooltip title="Number of active work orders assigned to the subcontractor">
          Open work orders
        </Tooltip>
      ),
      dataIndex: "open_work_orders",
      width: 166,
      align: "right",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "City",
      dataIndex: "city",
      width: 140,
      render: (text: string) => text ?? "",
    },
    {
      title: "State",
      dataIndex: "state",
      width: 100,
      render: (text: string) => text ?? "",
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 150,
      render: (text) =>
        text ? (
          <Tag className="default_tag" style={{ textTransform: "capitalize" }}>
            {text}
          </Tag>
        ) : (
          <Tag> - </Tag>
        ),
    },
    {
      title: (
        <Tooltip title="Primary point of contact at the subcontractor's side">
          Main Contact
        </Tooltip>
      ),
      dataIndex: "main_contracts",
      width: 180,
      render: (text) => (text ? text : "-"),
    },
    {
      title: (
        <Tooltip title="Contact person for billing and payment-related matters">
          Billing Contact
        </Tooltip>
      ),
      dataIndex: "billing_contracts",
      width: 180,
      render: (text) => (text ? text : "-"),
    },
    {
      title: "Category",
      dataIndex: "category_ids",
      width: 160,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  className={tagDetails?.color}
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },

    {
      title: (
        <Tooltip title="Revenue earned through the subcontractor, year-to-date">
          Earned revenue (YTD), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_ytd",
      width: 200,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Gross margin percentage on subcontractor work year-to-date">
          GM (YTD), %
        </Tooltip>
      ),
      dataIndex: "gross_margin",
      width: 130,
      render: (text) => (text ? text : "-"),
      align: "right",
    },

    {
      title: (
        <Tooltip title="Revenue earned through the subcontractor in the last 12 months">
          Earned revenue (R12M), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_r12m",
      width: 200,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Gross margin percentage on subcontractor work over the last 12 months">
          GM (R12M), %
        </Tooltip>
      ),
      dataIndex: "gross_margin_r12m",
      width: 200,
      render: (text) => (text ? text : "-"),
      align: "right",
    },

    {
      title: "Sub Status",
      dataIndex: "status",
      width: 147,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          overlayClassName="status_dropdown_overlay"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${text}_status`}
            ref={dropdownRef}
            style={{ textTransform: "capitalize" }}
          >
            {text}{" "}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{
            items:
              record?.status === "active"
                ? items
                : items.filter(
                    (item) => item?.key !== "1" && item?.key !== "1"
                  ),
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const fetchUserListing = (status: string) => {
    const payload = {
      company_id: companyId,
      offset: 0,
      limit: 10,
      status: status ?? "active",
    };
    dispatch(getSubcontractorListing(payload));
  };

  const deleteFunction = () => {
    setLoading(true);
    deleteSubcontractor(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage("Subcontractor deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          selectedRowKeys?.length > 0 ? (
            <>
              Are you sure you want to delete selected subcontractor?
              <br />
              This action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to delete <b>{tableRecord?.name}</b>?<br />
              <br />
              This action cannot be undone.
            </>
          )
        }
        btnText={
          selectedRowKeys?.length > 0
            ? `Delete ${
                selectedRowKeys?.length === 1
                  ? selectedRowKeys?.length + " subcontractor"
                  : selectedRowKeys?.length + " subcontractors"
              }`
            : "Delete subcontractor"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
    const payload = {
      status: tableRecord?.status === "active" ? "archived" : "active",
    };
    changeSubcontractorStatus(tableRecord?.id as string, payload)
      .then(() => {
        setLoading(false);
        setArchivePopUp(false);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage(
          `Subcontractor ${
            tableRecord?.status === "active" ? "archived" : "activated"
          } .`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading={tableRecord?.status === "active" ? "Archive" : "Activate"}
        paragraph={
          <>
            Are you sure you want to{" "}
            {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This subcontractor won’t be available for use.</>
            )}
          </>
        }
        btnText={`${
          tableRecord?.status === "active" ? "Archive" : "Activate"
        } subcontractor`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  useEffect(() => {
    dispatch(removeSubcontractListData());
  }, [dispatch]);

  useEffect(() => {
    if (companyId) {
      const payload = {
        status: filters?.status ?? "active",
        offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
        limit: limit,
        company_id: companyId,
        type: filters?.type ?? undefined,
        search_text: filters?.search_text ?? undefined,
      };
      dispatch(getSubcontractorListing(payload));
      console.log("payload", payload);
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  console.log("subListData", subListData);

  const onClickRow = () => {
    return {
      onClick: () => {
        navigate(`/subcontractor/profile/${companyId}`);
      },
    };
  };

  return (
    <DashboardTemplate loading={loading || subListData?.loader}>
      <Header
        title="Crews & Subs"
        description="View and manage your crews and subcontractors."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />
      <TableUi<ClientsType>
        columns={columns}
        data={{
          count: subListData?.count,
          data: subListData?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            companyId={companyId}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Add subcontractor"
        createButtonLink={`/add-subcontractor/${companyId}`}
        onClickRow={onClickRow}
      />
      {!subListData?.loader && subListData?.data?.length === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived subcontractors"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} subcontractors`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived subcontractors here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${
              selectedRowKeys.length === 1 ? "subcontractor" : "subcontractors"
            }`}{" "}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
    </DashboardTemplate>
  );
}
